/**
 * Lessons taught — the monthly per-teacher count (card dQzkKYcUNzuvhO0DEDES,
 * 2026-09-23). Anthony: "this is also how I will track how many lessons
 * Annelise teaches in each month."
 *
 * Reads what the session records SAY, never re-derives the rotation: the
 * nightly stamp (functions/lessonTeacher.js) writes `teacherId` +
 * `teacherSource` onto each record, and an admin corrects one here. A screen
 * that re-joined the rotation would disagree with the stored pay fact the
 * moment a released run was edited.
 *
 * PAID = ATTENDED ONLY (Anthony, 2026-09-23). Two numbers sit beside it on
 * purpose, because both make the paid number wrong until someone acts:
 *   - UNMARKED: a past lesson with no outcome. It may well have happened, so
 *     the paid count is a floor until these are marked. Future lessons are not
 *     "unmarked" — they have not happened yet.
 *   - NO TEACHER: a record the stamp could not attribute ('unresolved'), or
 *     one not stamped yet (a lesson opened today, before the nightly run). It
 *     is in nobody's count.
 *
 * SCHEDULED vs TAUGHT (2026-10-03; rule reversed that evening). `teacherId`
 * is who was SCHEDULED (the rotation, including approved coverage). `taughtBy`
 * is who gets the credit, stamped by functions/lessonTaughtBy.js: the
 * SCHEDULED teacher, whoever's login marks it, unless an admin picks someone
 * else on this page. (That morning it was the marker's teacher, which credited
 * three of Annelise's lessons to Grace; old records are re-credited.) An ATTENDED lesson counts
 * for the teacher who taught it; every other state (unmarked, no-show,
 * cancelled, upcoming) stays with the scheduled teacher, because nobody taught
 * it. A record from before tracking began has no `taughtBySource` and falls
 * back to the scheduled teacher, labelled as such. When the two differ the
 * lesson is a SUBSTITUTION and waits for an admin's OK (needsReview, which
 * mirrors the server's rule exactly).
 */

export const TEACHER_SOURCE_LABEL = Object.freeze({
	per_lesson: 'Rotation',
	source_slot: 'Rotation (makeup)',
	day_location: 'Rotation (day)',
	admin: 'Set by admin',
	unresolved: 'No teacher found',
});

const MONTH_RE = /^(\d{4})-(\d{2})$/;

/** "2026-09" -> { from: "2026-09-01", to: "2026-09-30" } (calendar-correct). */
export function monthRange(month) {
	const m = MONTH_RE.exec(month || '');
	if (!m) return null;
	const y = Number(m[1]);
	const mo = Number(m[2]);
	const last = new Date(y, mo, 0).getDate(); // day 0 of next month, LOCAL
	return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/** "2026-09" +/- n months, local arithmetic only. */
export function shiftMonth(month, n) {
	const m = MONTH_RE.exec(month || '');
	if (!m) return month;
	const d = new Date(Number(m[1]), Number(m[2]) - 1 + n, 1);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** The month containing a local Date. */
export function monthOf(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(month) {
	const m = MONTH_RE.exec(month || '');
	if (!m) return month;
	return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Does this record have a teacher we can count? */
export const hasTeacher = (r) => !!(r && r.teacherId && r.teacherSource && r.teacherSource !== 'unresolved');

export const TAUGHT_SOURCE_LABEL = Object.freeze({
	scheduled: 'Scheduled teacher',
	unscheduled: 'No teacher scheduled',
	marked: 'Marked attendance',
	backfill: 'Marked attendance',
	admin: 'Set by admin',
	unlinked: 'Marked by a login with no teacher profile',
});

/**
 * Who taught an ATTENDED lesson, and how we know.
 * @returns {{ teacherId: string|null, via: 'taught'|'schedule'|'unlinked'|null }}
 */
export function taughtTeacher(r) {
	if (!r || r.outcome !== 'attended') return { teacherId: null, via: null };
	if (r.taughtBySource === 'unlinked' || r.taughtBySource === 'unscheduled') return { teacherId: null, via: 'unlinked' };
	if (r.taughtBySource && r.taughtBy) return { teacherId: r.taughtBy, via: 'taught' };
	// Before tracking began: the scheduled teacher is the best record there is.
	return hasTeacher(r) ? { teacherId: r.teacherId, via: 'schedule' } : { teacherId: null, via: null };
}

/** The pair a substitution is about. MIRRORS functions/lessonTaughtBy.js. */
export function substitutionKey(r) {
	if (!r || r.outcome !== 'attended') return null;
	if (r.taughtBySource === 'admin') return null;
	if (r.taughtBySource === 'unscheduled') return `?|${r.teacherId || '?'}`;
	if (!r.taughtBy) return r.taughtBySource === 'unlinked' ? `?|${r.teacherId || '?'}` : null;
	if (!hasTeacher(r)) return null;
	return r.taughtBy === r.teacherId ? null : `${r.taughtBy}|${r.teacherId}`;
}

/** Does this lesson wait on an admin's OK? MIRRORS functions/lessonTaughtBy.js. */
export const needsReview = (r) => {
	const key = substitutionKey(r);
	return !!key && r.substitutionConfirmedFor !== key;
};

/**
 * @param records  session records for the month (any shape; legacy rows
 *                 without `origin` are dropped — they are MMS history, not
 *                 rotation lessons)
 * @param teachers [{ id, name }]
 * @param today    "YYYY-MM-DD", the studio's today — decides past vs future
 * @returns {{ rows, unattributed, totals, review }}
 *   rows: one per teacher who has any lesson OR is on the roster, sorted by
 *   name; each { teacherId, name, attended, covered, unmarked, noShow, cancelled, lessons }
 *   review: attended substitutions still waiting on an admin's OK, by date
 */
export function summarizeMonth(records, teachers, today) {
	const name = new Map((teachers || []).map((t) => [t.id, t.name || t.id]));
	const blank = (teacherId) => ({
		teacherId,
		name: teacherId ? name.get(teacherId) || 'Unknown teacher' : 'No teacher recorded',
		attended: 0,
		unmarked: 0,
		noShow: 0,
		cancelled: 0,
		upcoming: 0,
		covered: 0, // attended lessons this teacher taught that were scheduled for someone else
		lessons: [],
	});
	const byTeacher = new Map((teachers || []).map((t) => [t.id, blank(t.id)]));
	const unattributed = blank(null);

	const bucketFor = (id) => (id
		? byTeacher.get(id) || byTeacher.set(id, blank(id)).get(id)
		: unattributed);
	const rotationLessons = (records || []).filter((r) => r && r.origin && r.origin.slotId);
	const review = [];
	for (const r of rotationLessons) {
		// Attended -> whoever TAUGHT it; anything else -> whoever was scheduled.
		const taught = taughtTeacher(r);
		const bucket = r.outcome === 'attended'
			? bucketFor(taught.teacherId)
			: bucketFor(hasTeacher(r) ? r.teacherId : null);
		const past = !today || (r.date || '') <= today;
		if (needsReview(r)) review.push(r);
		if (r.outcome === 'attended' && taught.via === 'taught' && hasTeacher(r) && taught.teacherId !== r.teacherId) {
			bucket.covered += 1;
		}
		if (r.outcome === 'attended') bucket.attended += 1;
		else if (r.outcome === 'no_show') bucket.noShow += 1;
		else if (r.outcome === 'cancelled_family' || r.outcome === 'cancelled_cyv') bucket.cancelled += 1;
		else if (past) bucket.unmarked += 1;
		else bucket.upcoming += 1;
		bucket.lessons.push(r);
	}

	const sortLessons = (b) => b.lessons.sort((a, c) =>
		(a.date || '').localeCompare(c.date || '') || (a.time || '').localeCompare(c.time || ''));
	const rows = [...byTeacher.values()].sort((a, b) => a.name.localeCompare(b.name));
	rows.forEach(sortLessons);
	sortLessons(unattributed);

	const totals = rows.concat(unattributed).reduce((t, b) => ({
		attended: t.attended + b.attended,
		unmarked: t.unmarked + b.unmarked,
		unattributed: t.unattributed,
		review: t.review,
	}), { attended: 0, unmarked: 0, unattributed: unattributed.lessons.length, review: review.length });
	review.sort((a, c) => (a.date || '').localeCompare(c.date || '') || (a.time || '').localeCompare(c.time || ''));

	return { rows, unattributed, totals, review };
}

/** What a row's outcome reads as, including the two "not yet" states. */
export function outcomeText(r, today) {
	switch (r && r.outcome) {
		case 'attended': return 'Attended';
		case 'no_show': return 'No-show';
		case 'cancelled_family': return 'Cancelled · family';
		case 'cancelled_cyv': return 'Cancelled · CYV';
		default: return !today || (r.date || '') <= today ? 'Unmarked' : 'Upcoming';
	}
}
